import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { NoteKey } from '../hooks/useSound';

// Language configuration for the dropdown
const languageConfig: Record<string, { flag: string; nativeLabel: string }> = {
  en: { flag: '🇺🇸', nativeLabel: 'English' },
  'zh-TW': { flag: '🇹🇼', nativeLabel: '繁體中文' },
  vi: { flag: '🇻🇳', nativeLabel: 'Tiếng Việt' },
  fil: { flag: '🇵🇭', nativeLabel: 'Filipino' },
  id: { flag: '🇮🇩', nativeLabel: 'Bahasa Indonesia' },
  ms: { flag: '🇲🇾', nativeLabel: 'Bahasa Melayu' },
};

const availableLanguages = ['en', 'zh-TW', 'vi', 'fil', 'id', 'ms'];

interface NavItem {
  label: string;
  href: string;
  note: NoteKey;
  onAction?: () => void;
}

interface NavigationProps {
  language: string;
  onSetLanguage: (lang: string) => void;
  soundEnabled: boolean;
  onToggleSound: () => void;
  onPlayNote: (note: NoteKey) => void;
  navItems: NavItem[];
  contactLabel: string;
  cartItemCount?: number;
}

export default function Navigation({
  language,
  onSetLanguage,
  soundEnabled,
  onToggleSound,
  onPlayNote,
  navItems,
  contactLabel,
  cartItemCount,
}: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Detect scroll within the parent scroll container (not window)
  useEffect(() => {
    const scrollParent = navRef.current?.parentElement;
    if (!scrollParent) return;
    const handleScroll = () => setIsScrolled(scrollParent.scrollTop > 50);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = languageConfig[language] || languageConfig['en'];

  return (
    <nav
      ref={navRef}
      className={`sticky top-0 z-50 px-6 py-4 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-md bg-[var(--navy-deep)]/90 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <div
          className="flex items-center gap-2 cursor-pointer"
          onMouseEnter={() => onPlayNote('C6')}
        >
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--cyan-electric)] to-[var(--amber-warm)] flex items-center justify-center font-bold text-lg text-[var(--navy-deep)]">
            F
          </div>
          <span className="text-xl font-semibold tracking-tight">FLUXCONN</span>
        </div>

        {/* Nav Links */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item, i) =>
            item.onAction ? (
              <button
                key={i}
                onClick={item.onAction}
                onMouseEnter={() => onPlayNote(item.note)}
                className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200 relative group"
              >
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[var(--cyan-electric)] group-hover:w-3/4 transition-all duration-200" />
              </button>
            ) : (
              <a
                key={i}
                href={item.href}
                onMouseEnter={() => onPlayNote(item.note)}
                className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200 relative group"
              >
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[var(--cyan-electric)] group-hover:w-3/4 transition-all duration-200" />
              </a>
            )
          )}
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-4">
          {/* Cart */}
          <Link
            to="/cart"
            className="relative w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors"
            title="Shopping Cart"
            aria-label="Shopping Cart"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
            </svg>
            {(cartItemCount ?? 0) > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {cartItemCount}
              </span>
            )}
          </Link>

          {/* Sound toggle */}
          <button
            onClick={onToggleSound}
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors text-sm"
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
            aria-label={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>

          {/* Language selector dropdown */}
          <div className="relative">
            <button
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              onMouseEnter={() => onPlayNote('A5')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors"
              aria-label="Select language"
            >
              <span>{currentLang.flag}</span>
              <span className="hidden sm:inline">{currentLang.nativeLabel}</span>
              <svg
                className={`w-3 h-3 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {langMenuOpen && (
              <>
                {/* Backdrop - use screen-sized absolute to stay within panel */}
                <div className="fixed inset-0 z-40 w-screen h-screen" onClick={() => setLangMenuOpen(false)} />
                {/* Dropdown menu */}
                <div className="absolute right-0 mt-2 w-48 py-2 rounded-xl bg-[var(--navy-mid)] border border-white/10 shadow-xl z-50">
                  {availableLanguages.map((lang) => {
                    const config = languageConfig[lang];
                    const isActive = lang === language;
                    return (
                      <button
                        key={lang}
                        onClick={() => {
                          onSetLanguage(lang);
                          setLangMenuOpen(false);
                          onPlayNote('E5');
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/10 transition-colors ${
                          isActive ? 'text-[var(--cyan-electric)]' : 'text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{config.flag}</span>
                        <span className="flex-1">{config.nativeLabel}</span>
                        {isActive && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Contact CTA */}
          <a
            href="#contact"
            onMouseEnter={() => onPlayNote('C6')}
            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[var(--cyan-electric)] to-cyan-400 text-[var(--navy-deep)] font-medium transition-all hover:shadow-lg hover:shadow-cyan-500/25"
          >
            {contactLabel}
          </a>
        </div>
      </div>
    </nav>
  );
}
